import React, { useEffect, useSyncExternalStore } from 'react';
import styled, { keyframes } from 'styled-components';

// Carregamento do site. Fica na área do conteúdo, entre o cabeçalho e o
// rodapé, que continuam à vista (o site não parece "um sistema abrindo").
//
// As páginas continuam usando <Loading /> enquanto buscam os dados: cada um
// montado é um "pendente". O carregamento termina quando não há pendente e a
// primeira tela está pronta: fontes e imagens que aparecem sem rolar (banner,
// capa, destaques). O resto continua carregando depois. Teto de 8 s.
//
// Enquanto carrega, a área do conteúdo tem altura fixa (ver ConteudoDoSite):
// a página por trás pode nascer vazia, virar <Loading /> e depois virar
// conteúdo sem o rodapé pular -- era isso que fazia a tela tremer.

const ESPERA_MAXIMA = 8000;
// Na troca de página, só mostra se o carregamento passar disto: página que já
// estava em cache não pisca.
const ATRASO_PARA_MOSTRAR = 180;
const TEMPO_MINIMO = 450; // depois de aparecer, fica pelo menos isto
const SAIDA = 400; // o fade da saída

// ------------------------------------------------------------------- estado

// 'carregando' → 'saindo' (fade) → 'pronto'. A primeira abertura já começa
// carregando (o index.html mostra a mesma barra até o React montar).
let fase = 'carregando';
// Quando o carregamento atual começou: na primeira abertura, o início da
// página (a barra do index.html já vinha andando desde então).
let mostradoEm = Date.now() - performance.now();
const ouvintes = new Set();
const mudar = (nova) => {
  if (nova === fase) return;
  fase = nova;
  if (nova === 'carregando') mostradoEm = Date.now();
  ouvintes.forEach((o) => o());
};
const assinar = (o) => {
  ouvintes.add(o);
  return () => ouvintes.delete(o);
};
export const useFaseDoCarregamento = () => useSyncExternalStore(assinar, () => fase);

let pendentes = 0;
let rodada = 0; // muda a cada novo carregamento: cancela uma espera antiga
let timerMostrar = null;
let timerSaida = null;

const esperar = (ms) => new Promise((r) => setTimeout(r, ms));
const proximoQuadro = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

// Imagens que aparecem na primeira tela: <img> e fundos por CSS. "Na tela"
// nos dois sentidos: os slides seguintes de um carrossel ficam à direita, fora
// dela, e só carregam quando chegam (esperar por eles prendia a Home).
const imagensDaPrimeiraTela = () => {
  const altura = window.innerHeight;
  const largura = window.innerWidth;
  const urls = new Set();
  const espera = [];
  document.querySelectorAll('[data-conteudo] *').forEach((el) => {
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height || r.top >= altura || r.bottom <= 0 || r.left >= largura || r.right <= 0) return;
    if (el.tagName === 'IMG') {
      if (!el.complete) {
        espera.push(
          new Promise((ok) => {
            el.addEventListener('load', ok, { once: true });
            el.addEventListener('error', ok, { once: true });
          }),
        );
      }
      return;
    }
    const fundo = getComputedStyle(el).backgroundImage;
    if (fundo && fundo !== 'none') {
      fundo.replace(/url\(["']?([^"')]+)["']?\)/g, (_, url) => urls.add(url));
    }
  });
  urls.forEach((url) => {
    espera.push(
      new Promise((ok) => {
        const img = new Image();
        img.onload = ok;
        img.onerror = ok;
        img.src = url;
        if (img.complete) ok();
      }),
    );
  });
  return Promise.all(espera);
};

const quandoPronto = async () => {
  const minha = ++rodada;
  // Dá tempo de a página trocar o <Loading /> pelo conteúdo e de algum efeito
  // abrir um novo carregamento logo em seguida (ex.: o token chegando).
  await esperar(120);
  await proximoQuadro();
  if (minha !== rodada || pendentes > 0 || fase !== 'carregando') return;

  await Promise.race([
    Promise.all([imagensDaPrimeiraTela(), document.fonts?.ready]),
    esperar(ESPERA_MAXIMA),
  ]);
  const falta = TEMPO_MINIMO - (Date.now() - mostradoEm);
  if (falta > 0) await esperar(falta);
  if (minha !== rodada || pendentes > 0) return;
  mudar('saindo');
  timerSaida = setTimeout(() => mudar('pronto'), SAIDA);
};

const comecar = () => {
  pendentes += 1;
  rodada += 1; // cancela uma espera em andamento
  if (fase === 'saindo') {
    clearTimeout(timerSaida);
    mudar('carregando');
  } else if (fase === 'pronto' && !timerMostrar) {
    timerMostrar = setTimeout(() => {
      timerMostrar = null;
      if (pendentes > 0) mudar('carregando');
    }, ATRASO_PARA_MOSTRAR);
  }
};

const terminar = () => {
  pendentes = Math.max(0, pendentes - 1);
  if (pendentes === 0) {
    clearTimeout(timerMostrar);
    timerMostrar = null;
    quandoPronto();
  }
};

// Segura o carregamento até a promessa terminar, para o que ainda decide uma
// imagem da primeira tela (ex.: o banner da Home, que descobre se a arte é GIF
// antes de escolher o endereço final; sem isto ela saía com o banner em branco).
export const aguardar = (promessa) => {
  comecar();
  Promise.resolve(promessa).finally(terminar);
  return promessa;
};

// Primeira abertura: tira a barra do index.html (a do React está no mesmo
// lugar) e, se nenhuma página abrir um <Loading />, termina quando a primeira
// tela estiver pronta.
export const useTelaDeCarregamento = () => {
  useEffect(() => {
    document.getElementById('carregando-inicial')?.remove();
    if (pendentes === 0) quandoPronto();
  }, []);
};

// O lugar do conteúdo enquanto os dados não chegam, quando o carregamento nem
// chegou a aparecer (página rápida): segura o rodapé mais ou menos onde ele
// fica durante o carregamento.
export const Loading = () => {
  useEffect(() => {
    comecar();
    return terminar;
  }, []);
  return <div style={{ minHeight: 'max(260px, calc(100vh - 470px))' }} aria-hidden="true" />;
};

// ------------------------------------------------------------------ desenho

// A barra avança sozinha até perto do fim enquanto espera e completa na saída.
// Começa adiantada pelo tempo que o carregamento já leva: na primeira abertura
// continua de onde a barra do index.html estava, sem voltar ao zero quando o
// React monta.
const avanca = keyframes`
  from { width: 0; }
  to { width: 88%; }
`;

const Cobertura = styled.div`
  position: absolute;
  inset: 0;
  /* acima do conteúdo; o botão "voltar ao topo" do rodapé (z 10) fica por cima */
  z-index: 5;
  background: #f8faf9;
  opacity: ${({ $saindo }) => ($saindo ? 0 : 1)};
  transition: opacity ${SAIDA}ms ease;
  pointer-events: ${({ $saindo }) => ($saindo ? 'none' : 'auto')};
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
`;

/* No meio da área do conteúdo (a mesma posição da barra do index.html). */
const Centro = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;

  p {
    margin: 16px 0 0;
    font-size: 14px;
    letter-spacing: 0.04em;
    color: #143e2d;
  }
`;

const Barra = styled.div`
  position: relative;
  width: min(320px, 70vw);
  height: 4px;
  border-radius: 2px;
  background: #dce5dd;
  overflow: hidden;

  span {
    position: absolute;
    inset: 0 auto 0 0;
    width: ${({ $saindo }) => ($saindo ? '100%' : '0')};
    background: #00893a;
    animation: ${({ $saindo }) => ($saindo ? 'none' : avanca)} 6s cubic-bezier(0.1, 0.7, 0.2, 1) forwards;
    transition: width 0.25s ease;
  }

  span::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 8px;
    height: 100%;
    background: #fbb81c;
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
      width: 60%;
    }
  }
`;

export const TelaDeCarregamento = () => {
  const atual = useFaseDoCarregamento();
  if (atual === 'pronto') return null;
  return (
    <Cobertura $saindo={atual === 'saindo'} role="status" aria-live="polite">
      <Centro>
        <Barra $saindo={atual === 'saindo'}>
          <span style={{ animationDelay: `-${Math.min(5500, Date.now() - mostradoEm)}ms` }} />
        </Barra>
        <p>Carregando...</p>
      </Centro>
    </Cobertura>
  );
};
